import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogOut, Phone, Mail, Trash2, Check, RotateCcw, Inbox as InboxIcon, Send } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  new: "bg-brand-secondary/15 text-brand-secondary",
  contacted: "bg-brand-primary/10 text-brand-primary",
  archived: "bg-brand-muted/20 text-brand-muted",
};

export default function Inbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/appointments`, { credentials: "include" });
      if (res.status === 401 || res.status === 403) {
        navigate("/login", { replace: true });
        return;
      }
      const data = await res.json();
      setItems(data);
    } catch (e) {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/appointments/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    try {
      const res = await fetch(`${API}/appointments/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const sendReply = async (id, template) => {
    const toastId = toast.loading("Sending email…");
    try {
      const res = await fetch(`${API}/appointments/${id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ template }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: "contacted" } : it)));
      toast.success(`Email sent to ${data.to}`, { id: toastId });
    } catch {
      toast.error("Could not send email.", { id: toastId });
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/login", { replace: true });
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = {
    all: items.length,
    new: items.filter((i) => i.status === "new").length,
    contacted: items.filter((i) => i.status === "contacted").length,
    archived: items.filter((i) => i.status === "archived").length,
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="inbox-page">
      <Toaster position="top-center" richColors />
      <header className="border-b border-brand-primary/10 bg-white/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="NeuroNest" className="h-10 w-auto object-contain" />
            <span className="hidden sm:inline text-brand-muted text-sm">Enquiry Inbox</span>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-brand-primary/20 text-brand-primary px-5 py-2.5 text-sm hover:bg-brand-primary hover:text-brand-bg transition-colors"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-light text-4xl text-brand-primary">Appointment requests</h1>
            <p className="text-brand-ink/60 mt-2">Manage enquiries submitted from your website.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["all", "new", "contacted", "archived"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`rounded-full px-4 py-2 text-sm capitalize transition-colors ${
                  filter === f
                    ? "bg-brand-primary text-brand-bg"
                    : "border border-brand-primary/15 text-brand-primary hover:bg-brand-primary/5"
                }`}
              >
                {f} ({counts[f]})
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center">
            <div className="h-10 w-10 rounded-full border-2 border-brand-primary/20 border-t-brand-primary animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center" data-testid="inbox-empty">
            <InboxIcon className="h-12 w-12 mx-auto text-brand-muted/50" strokeWidth={1.25} />
            <p className="mt-4 text-brand-muted">No enquiries here yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filtered.map((it) => (
              <div
                key={it.id}
                data-testid={`enquiry-${it.id}`}
                className="rounded-2xl bg-white border border-brand-primary/10 p-6 shadow-[0_4px_20px_rgba(46,74,61,0.05)]"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-xl text-brand-primary">{it.name}</h3>
                      <span className={`rounded-full px-3 py-0.5 text-xs capitalize ${statusStyles[it.status] || ""}`}>
                        {it.status}
                      </span>
                      {it.service && (
                        <span className="rounded-full bg-brand-accent/50 text-brand-primary px-3 py-0.5 text-xs">
                          {it.service}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-brand-ink/70">
                      <a href={`tel:${it.phone}`} className="inline-flex items-center gap-2 hover:text-brand-primary">
                        <Phone className="h-4 w-4" strokeWidth={1.5} /> {it.phone}
                      </a>
                      <a href={`mailto:${it.email}`} className="inline-flex items-center gap-2 hover:text-brand-primary">
                        <Mail className="h-4 w-4" strokeWidth={1.5} /> {it.email}
                      </a>
                      {it.child_age && <span>Child&apos;s age: {it.child_age}</span>}
                    </div>
                    {it.message && (
                      <p className="mt-3 text-brand-ink/80 leading-relaxed bg-brand-bg rounded-lg p-3 max-w-xl">
                        {it.message}
                      </p>
                    )}
                    <p className="mt-3 text-xs text-brand-muted">
                      {new Date(it.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`reply-trigger-${it.id}`}
                          title="Send a reply email"
                          className="inline-flex items-center gap-1.5 rounded-full bg-brand-secondary text-white px-3 py-2 text-xs hover:brightness-95 transition-all"
                        >
                          <Send className="h-3.5 w-3.5" strokeWidth={1.75} /> Reply
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-60">
                        <DropdownMenuLabel>Send a quick email</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem data-testid={`reply-call_back-${it.id}`} onClick={() => sendReply(it.id, "call_back")}>
                          We&apos;ll call you shortly
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`reply-schedule-${it.id}`} onClick={() => sendReply(it.id, "schedule")}>
                          Let&apos;s schedule your visit
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`reply-info-${it.id}`} onClick={() => sendReply(it.id, "info")}>
                          About our services
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {it.status !== "contacted" && (
                      <button
                        onClick={() => setStatus(it.id, "contacted")}
                        data-testid={`mark-contacted-${it.id}`}
                        title="Mark as contacted"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-primary text-brand-bg px-3 py-2 text-xs hover:bg-[#1f332a] transition-colors"
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={2} /> Contacted
                      </button>
                    )}
                    {it.status !== "archived" ? (
                      <button
                        onClick={() => setStatus(it.id, "archived")}
                        data-testid={`archive-${it.id}`}
                        title="Archive"
                        className="inline-flex items-center rounded-full border border-brand-primary/20 text-brand-primary px-3 py-2 text-xs hover:bg-brand-primary/5 transition-colors"
                      >
                        Archive
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatus(it.id, "new")}
                        data-testid={`restore-${it.id}`}
                        title="Restore"
                        className="inline-flex items-center gap-1.5 rounded-full border border-brand-primary/20 text-brand-primary px-3 py-2 text-xs hover:bg-brand-primary/5 transition-colors"
                      >
                        <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} /> Restore
                      </button>
                    )}
                    <button
                      onClick={() => remove(it.id)}
                      data-testid={`delete-${it.id}`}
                      title="Delete"
                      className="inline-flex items-center rounded-full border border-red-200 text-red-500 px-3 py-2 text-xs hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
